/**
 * slots — the SLOT INBOX, and the store a host can share across several pages.
 *
 * Lives outside `interpreter.jsx` so the studio can hold the SAME store the
 * interpreter reduces into. Its phone strip is [ live preview | widget data |
 * focus | connection… ] — top-down views of ONE running widget — and each card
 * used to be its own `ViewIRPage` with its own inbox, so a filter tapped on the
 * live card left the focus card showing an unfiltered page. One store shared by
 * every card is what makes them the same app in the same state.
 *
 * Two halves, because a selection has two homes:
 *   slotState  what a connection DELIVERED (`shelves.applyFilter`) — the data
 *              the grid filters by;
 *   ui         what the user TAPPED, keyed by node id — the tile's own
 *              highlight. Kit components keep this in local `useState` (that is
 *              what makes a chip bar droppable into any blueprint), which
 *              resets when a page change unmounts them while the applied filter
 *              lives on. Above the page it survives the round-trip.
 */
import { useCallback, useMemo, useReducer, useState } from 'react';

const EMPTY_UI = {};

/**
 * A slot keeps the latest payload, and — because several sources may
 * legitimately drive ONE slot (three chip bars combining into a grid's single
 * `applyFilter`) — also the latest payload PER SOURCE in `bySource`. Without
 * that, the last chip tapped would silently replace the other two dimensions
 * instead of combining with them.
 */
export function slotReducer(state, action) {
  if (action.type === 'reset') return {};
  const { nodeId, slot, payload, seq, from } = action;
  const forNode = state[nodeId] || {};
  const bySource = { ...(forNode[slot]?.bySource || {}) };
  if (from) bySource[from] = payload;
  return { ...state, [nodeId]: { ...forNode, [slot]: { payload, seq, from, bySource } } };
}

/**
 * The bundle a host hands `ViewIRPage` as `interaction` to own both halves
 * itself. Omit it and each page keeps its own inbox and its components their
 * own local selection — the app path, unchanged.
 */
export function useInteractionStore() {
  const [slotState, dispatch] = useReducer(slotReducer, {});
  const [ui, setUiState] = useState(EMPTY_UI);
  const setUi = useCallback((nodeId, value) => setUiState(
    (prev) => (prev[nodeId] === value ? prev : { ...prev, [nodeId]: value }),
  ), []);
  return useMemo(() => ({ slotState, dispatch, ui, setUi }), [slotState, dispatch, ui, setUi]);
}
